/**
 * ListMigrateProgressOverlay — full-screen Spanish overlay for list migration.
 *
 * Shown while the backend backfills intent metadata on legacy free-text items:
 *  - streaming: "Actualizando tu lista…" + determinate
 *    progress bar driven by the `percentage` prop (updates per SSE event)
 *  - error: "Error al migrar. Intentar de nuevo." + retry button
 *  - partial: Spanish warning + reload action (the page closes the overlay on
 *    the terminal partial event per the spec; the visual is offered for
 *    embedding flows that keep it visible)
 */

import { MainLogo } from "modules/main/assets/main_logo";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

export type MigrateOverlayStatus = "streaming" | "error" | "partial";

export interface ListMigrateProgressOverlayProps {
    /** Visual state of the overlay. */
    status: MigrateOverlayStatus;
    /** Progress bar fill, 0-100 (updates with each migration_item event). */
    percentage: number;
    /** Current item being migrated (streaming detail line). */
    message?: string;
    /** Number of items whose embedding failed (partial state). */
    failedCount?: number;
    /** Restarts the migration stream (error state). */
    onRetry?: () => void;
    /** Reload action (partial state). */
    onReload?: () => void;
}

export const ListMigrateProgressOverlay = ({
    status,
    percentage,
    message,
    failedCount,
    onRetry,
    onReload,
}: ListMigrateProgressOverlayProps) => {
    return (
        <div
            role="status"
            aria-live="polite"
            data-testid="migrate-progress-overlay"
            className="fixed inset-0 z-50 bg-white/70 backdrop-blur-md flex flex-col items-center justify-center gap-4 px-8"
        >
            <div className="animate-pulse">
                <MainLogo classname="w-20" />
            </div>

            {status === "streaming" && (
                <div className="flex flex-col items-center gap-2 w-72">
                    <p className="text-sm font-semibold text-center">
                        Actualizando tu lista…
                    </p>
                    <Progress value={percentage} className="w-full" />
                    <p className="text-xs text-muted-foreground">{percentage}%</p>
                    {message ? (
                        <p className="text-xs text-muted-foreground text-center truncate max-w-full">
                            {message}
                        </p>
                    ) : null}
                </div>
            )}

            {status === "error" && (
                <div className="flex flex-col items-center gap-3 w-72">
                    <p className="text-sm font-semibold text-center text-destructive">
                        Error al migrar. Intentar de nuevo.
                    </p>
                    <Button variant="outline" onClick={onRetry}>
                        Intentar de nuevo
                    </Button>
                </div>
            )}

            {status === "partial" && (
                <div className="flex flex-col items-center gap-3 w-72">
                    <p className="text-sm font-semibold text-center">
                        La migración quedó incompleta
                        {failedCount && failedCount > 0 ? ` (${failedCount} producto${failedCount !== 1 ? "s" : ""})` : ""}.
                    </p>
                    <p className="text-xs text-muted-foreground text-center">
                        Algunos productos no se pudieron migrar. Recarga para volver a intentarlo.
                    </p>
                    <Button variant="outline" onClick={onReload}>
                        Recargar
                    </Button>
                </div>
            )}
        </div>
    );
};
